/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from "react";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    Tooltip,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import { Carousel } from "react-responsive-carousel";
import { maskBRL } from "../../utils/mask";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth";
import { IArquivo } from "../../types";
import "leaflet/dist/leaflet.css";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import mapPin from "../../assets/mapPin.svg";

export type MapaImoveisProps = {
    imoveis: {
        id: number;
        id_display: string;
        descricao: string;
        latitude: number;
        longitude: number;
        valor_venda: number;
        valor_aluguel: number;
        imagens: IArquivo[];
        is_venda: number;
        is_aluguel: number;
    }[];
};

export default function MapaImoveis({ imoveis }: MapaImoveisProps) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const mapRef = useRef<any>(null);

    const createClusterCustomIcon = (cluster: any) =>
        new L.DivIcon({
            html: `<div style="
                background-color: #0e2244;
                color: white;
                border-radius: 50%;
                width: 60px;
                height: 60px;
                display: flex;
                justify-content: center;
                align-items: center;
                font-weight: bold;
                border: 2px solid white;
            ">
                ${cluster.getChildCount()}
            </div>`,
            className: "custom-cluster-icon",
            iconSize: [60, 60],
        });

    const defaultCenter: [number, number] = [-8.0476, -34.877];

    const imoveisValidos = imoveis.filter(
        (imovel) =>
            Number.isFinite(imovel.latitude) &&
            Number.isFinite(imovel.longitude),
    );

    const mapCenter: [number, number] =
        imoveisValidos.length > 0
            ? [imoveisValidos[0].latitude, imoveisValidos[0].longitude]
            : defaultCenter;

    const pin = L.icon({
        iconUrl: mapPin,
        iconSize: [50, 50],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40],
    });

    // Bloqueia scroll indesejado ao interagir com o mapa
    useEffect(() => {
        const mapContainer = mapRef.current?.getContainer?.();
        if (mapContainer) {
            const handleClick = (e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
            };
            mapContainer.addEventListener("click", handleClick);
            mapContainer.addEventListener("touchstart", handleClick);

            return () => {
                mapContainer.removeEventListener("click", handleClick);
                mapContainer.removeEventListener("touchstart", handleClick);
            };
        }
    }, []);

    return (

        <MapContainer
            center={mapCenter}
            zoom={12}
            style={{ width: "100%", height: "100%" }}
            ref={mapRef}
            dragging={true}
            touchZoom={true}
            scrollWheelZoom={false}
            doubleClickZoom={true}
            zoomControl={true}
        >
            <TileLayer
                attribution='&copy; <a href="https://www.carto.com/">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=cb1_27id_1_0ce26f1e1fbfb85845fb05de"
            />

            <MarkerClusterGroup iconCreateFunction={createClusterCustomIcon}>
                {imoveisValidos.map((imovel) => {
                    const labelText =
                        maskBRL(imovel.valor_venda || imovel.valor_aluguel);

                    return (
                        <Marker
                            key={imovel.id}
                            position={[imovel.latitude, imovel.longitude]}
                            icon={pin}
                            eventHandlers={{
                                click: (e) => {
                                    e.originalEvent.stopPropagation();
                                    e.originalEvent.preventDefault();
                                    const marker = e.target;
                                    marker.openPopup(); // Abre popup sem mexer no React
                                },
                            }}
                        >
                            <Tooltip direction="top" offset={[0, -40]} permanent>
                                {labelText}
                            </Tooltip>

                            <Popup closeOnClick={true}>
                                {(imovel.imagens &&
                                    imovel.imagens[0]?.url &&
                                    imovel.imagens[0].url !== "https://www.nogueiracorretores.com.br/") ? (
                                    <div style={{ borderRadius: "12px", overflow: "hidden" }}>
                                        <Carousel
                                            autoPlay
                                            interval={5000}
                                            showThumbs={false}
                                            infiniteLoop
                                            showStatus={false}
                                            className="popup-carousel"
                                        >
                                            {imovel.imagens.map((file, idx) => (
                                                <img
                                                    key={idx}
                                                    src={file.url}
                                                    alt="Imóvel"
                                                    onClick={() => {
                                                        const url = user
                                                            ? `/perfil/${user.id_corretor}/imovel/${imovel.id}`
                                                            : `/imovel/${imovel.id}`;
                                                        window.open(url, "_blank");
                                                    }}
                                                />
                                            ))}
                                        </Carousel>
                                    </div>
                                ) : (
                                    <img
                                        className="img-placeholder"
                                        src="/images/placeholder-image-nogueira.png"
                                        alt="placeholder-nogueira"
                                        style={{ width: "100%", height: "160px", objectFit: "cover" }}
                                        onClick={() =>
                                            navigate(user
                                                ? `/perfil/${user.id_corretor}/imovel/${imovel.id}`
                                                : `/imovel/${imovel.id}`
                                            )
                                        }
                                    />
                                )}

                                <div className="popup-info" onClick={() => {
                                    const url = user
                                        ? `/perfil/${user.id_corretor}/imovel/${imovel.id}`
                                        : `/imovel/${imovel.id}`;
                                    window.open(url, "_blank");
                                }}>
                                    <h3>
                                        {(imovel.id_display || imovel.id) + " - " + imovel.descricao}
                                    </h3>
                                    <p>A partir de: <br></br> <span>{maskBRL(imovel.is_venda === 1 ? imovel.valor_venda : imovel.is_aluguel === 1 ? imovel.valor_aluguel : undefined)}</span></p>
                                </div>
                            </Popup>

                        </Marker>
                    );
                })}
            </MarkerClusterGroup>
        </MapContainer>

    );
}
